/**
 * Gift Popup Logic (Dawn-adapted)
 * - Uses Shopify AJAX API (/cart.js, /cart/add.js)
 * - Shows popup based on cart total (>= 100 CHF)
 * - Only shows once per browser session (localStorage)
 * - Adds predefined gift variants to cart
 * - After adding, re-renders Dawn cart drawer + cart icon bubble
 */

(function () {
    /**
     * ─────────────────────────────────────────────
     *  TEST MODE
     *  Set to true  → popup opens on EVERY cart change (ignores localStorage)
     *  Set to false → popup shows only ONCE per session (production behaviour)
     * ─────────────────────────────────────────────
     */
    const TEST_MODE = false;

    const GIFT_THRESHOLD_CHF = 100;
    const LOCAL_STORAGE_KEY  = 'balibayGiftPopupShown';
    const RESET_QUERY_PARAM  = 'gift_popup_reset';
    const POPUP_DELAY_MS     = 2000; // 2-second delay before popup opens

    // Timer handle — cleared whenever a new cart update arrives so multiple
    // rapid updates don't stack up multiple popups.
    var gpPopupDelayTimer = null;
  
    /**
     * Read all gift variant IDs directly from the popup DOM.
     * Set by gift-popup.liquid via data-gift-variant-id — no hardcoding needed.
     */
    function getGiftVariantIds() {
      var ids = [];
      document.querySelectorAll('[data-gift-variant-id]').forEach(function (card) {
        var id = parseInt(card.getAttribute('data-gift-variant-id'), 10);
        if (!isNaN(id)) ids.push(id);
      });
      return ids;
    }
  
    /**
     * Helper: Fetch cart data from /cart.js
     * Returns a Promise resolving to the cart object.
     */
    function fetchCart() {
      return fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Failed to fetch cart data');
        }
        return response.json();
      });
    }
  
    /**
     * Convert Shopify's cents integer to CHF number.
     * Example: 10000 -> 100.00
     */
    function centsToChf(cents) {
      return (cents || 0) / 100;
    }
  
    /**
     * Testing helper: allow resetting localStorage via URL param.
     * Example: add ?gift_popup_reset=1 to any page URL.
     */
    function maybeResetPopupStateFromUrl() {
      try {
        var params = new URLSearchParams(window.location.search);
        if (params.get(RESET_QUERY_PARAM) === '1' || params.get(RESET_QUERY_PARAM) === 'true') {
          if (window.localStorage)   localStorage.removeItem(LOCAL_STORAGE_KEY);
          if (window.sessionStorage) { sessionStorage.removeItem(LOCAL_STORAGE_KEY + '_below'); sessionStorage.removeItem('balibayGiftPopupAutoOpened'); }
        }
      } catch (e) {
        // Ignore URL parsing/storage errors
      }
    }

    /**
     * Dawn helper: re-render cart drawer via sections API, then manually
     * update the cart count badge.
     *
     * We intentionally do NOT re-render cart-icon-bubble via sections because
     * Dawn's section template uses its own SVG icon which would overwrite the
     * ETX theme's custom Font Awesome bag icon. Instead we update the count
     * directly in the existing DOM.
     */
    function updateDawnCartSections() {
      var searchParams = new URLSearchParams();
      searchParams.set('sections', 'cart-drawer');

      var url = window.location.pathname + '?' + searchParams.toString();

      return fetch(url, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Failed to fetch cart sections');
          return response.json();
        })
        .then(function (sectionsHtml) {
          // Update cart drawer
          var html = sectionsHtml['cart-drawer'];
          if (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newDrawer = doc.querySelector('cart-drawer');
            var currentDrawer = document.querySelector('cart-drawer');
            if (currentDrawer && newDrawer && currentDrawer.parentNode) {
              currentDrawer.parentNode.replaceChild(newDrawer, currentDrawer);
            }
          }

          // Update cart count badge without touching the icon markup
          return fetchCart().then(function (cart) {
            var count = cart.item_count || 0;
            var bubble = document.querySelector('.cart-count-bubble');
            if (bubble) {
              var spans = bubble.querySelectorAll('span');
              if (spans[0]) spans[0].textContent = count;
              if (spans[1]) spans[1].textContent = count + ' Artikel';
            }
            // Also show/hide the bubble if cart goes empty/non-empty
            var iconBubble = document.getElementById('cart-icon-bubble');
            if (iconBubble) {
              var existingBubble = iconBubble.querySelector('.cart-count-bubble');
              if (existingBubble) {
                existingBubble.classList.toggle('hidden', count === 0);
              }
            }
          });
        });
    }
  
    /**
     * Dawn helper: open cart drawer after update.
     */
    function openDawnCartDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
  
      // Dawn cart drawer is a custom element with an .open() method.
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }

      // Fallback for themes that use an attribute/class toggle.
      drawer.setAttribute('open', '');
    }
  
    /**
     * Swiper instance for the gift product slider.
     * Initialised lazily on first openGiftPopup() so Swiper can calculate
     * dimensions correctly (the overlay is display:none on page load).
     */
    var gpSwiper = null;

    function initGpSwiper() {
      var container = document.querySelector('.gp-swiper');
      if (!container) return;

      // Already initialised — just tell Swiper to recalculate layout
      if (gpSwiper) {
        gpSwiper.update();
        return;
      }

      // Swiper is loaded globally by the theme (swiper-bundle.min.js)
      if (typeof Swiper === 'undefined') return;

      gpSwiper = new Swiper(container, {
        spaceBetween: 10,
        loop: true,
        loopAddBlankSlides: false,
        navigation: {
          prevEl: '.gp-nav--prev',
          nextEl: '.gp-nav--next',
        },
        breakpoints: {
          0:   { slidesPerView: 1.2, slidesPerGroup: 1 },
          480: { slidesPerView: 2,   slidesPerGroup: 1 },
          560: { slidesPerView: 3,   slidesPerGroup: 1 },
          680: { slidesPerView: 4,   slidesPerGroup: 1 },
        }
      });
    }

    /**
     * SVG icons reused across button states
     */
    // Use the site's own SVG sprite icons — identical to Best Seller product cards
    var SVG_BAG   = '<svg class="icon icon-bag" width="24" height="25" aria-hidden="true"><use xlink:href="#shoppingBag"></use></svg>';
    var SVG_CHECK = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" style="display:block;width:20px;height:20px;fill:none;stroke:#fff;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round"><polyline points="20 6 9 17 4 12"/></svg>';
    var SVG_SPIN  = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke-opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke-opacity="1"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg>';

    /**
     * How many free gifts is the customer allowed based on cart total?
     * Rule: exactly 1 gift once the cart reaches 100 CHF (matches the Shopify
     * discount, which is limited to 1 use per order).
     */
    var MAX_GIFTS_PER_ORDER = 1;
    function calcAllowedGifts(totalChf) {
      var cards = document.querySelectorAll('[data-gift-variant-id]');
      var maxGifts = Math.min(cards.length, MAX_GIFTS_PER_ORDER);
      return Math.min(Math.floor(totalChf / GIFT_THRESHOLD_CHF), maxGifts);
    }

    /**
     * Count how many items in the cart are marked as gifts (_is_gift property).
     * We use a line item property instead of variant ID matching so that a
     * customer who also bought a gift-eligible product as a paid item is NOT
     * counted as having already claimed a gift.
     */
    function countGiftsInCart(cart) {
      var count = 0;
      if (cart && cart.items) {
        cart.items.forEach(function (item) {
          if (item.properties && item.properties['_is_gift'] === 'true') {
            count += item.quantity;
          }
        });
      }
      return count;
    }

    /**
     * Find the line item key of the currently selected gift (marked _is_gift).
     * Returns null if none found.
     */
    function findGiftItemKey(cart) {
      if (!cart || !cart.items) return null;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.properties && item.properties['_is_gift'] === 'true') {
          return item.key;
        }
      }
      return null;
    }

    /**
     * Open popup and set correct state for every card button.
     * Called with the full cart object so we can calculate limits accurately.
     */
    function openGiftPopup(cart) {
      var overlay = document.getElementById('gift-popup-overlay');
      if (!overlay) return;

      var totalChf      = centsToChf(cart && cart.total_price);
      var allowed       = calcAllowedGifts(totalChf);
      var alreadyAdded  = countGiftsInCart(cart);
      var remaining     = Math.max(allowed - alreadyAdded, 0);
      var isAbove       = allowed >= 1;

      // Show correct heading / text
      var eyebrowAbove  = overlay.querySelector('.gift-popup-eyebrow--above-threshold');
      var eyebrowBelow  = overlay.querySelector('.gift-popup-eyebrow--below-threshold');
      var headingAbove  = overlay.querySelector('.gift-popup-heading--above-threshold');
      var headingBelow  = overlay.querySelector('.gift-popup-heading--below-threshold');
      var textAbove     = overlay.querySelector('.gift-popup-text--above-threshold');
      var textBelow     = overlay.querySelector('.gift-popup-text--below-threshold');
      var subtextAbove  = overlay.querySelector('.gift-popup-subtext--above-threshold');

      if (isAbove) {
        eyebrowAbove && eyebrowAbove.classList.remove('is-hidden');
        headingAbove && headingAbove.classList.remove('is-hidden');
        textAbove    && textAbove.classList.remove('is-hidden');
        subtextAbove && subtextAbove.classList.remove('is-hidden');
        eyebrowBelow && eyebrowBelow.classList.add('is-hidden');
        headingBelow && headingBelow.classList.add('is-hidden');
        textBelow    && textBelow.classList.add('is-hidden');
        overlay.classList.remove('is-below-threshold');
      } else {
        eyebrowAbove && eyebrowAbove.classList.add('is-hidden');
        headingAbove && headingAbove.classList.add('is-hidden');
        textAbove    && textAbove.classList.add('is-hidden');
        subtextAbove && subtextAbove.classList.add('is-hidden');
        eyebrowBelow && eyebrowBelow.classList.remove('is-hidden');
        headingBelow && headingBelow.classList.remove('is-hidden');
        textBelow    && textBelow.classList.remove('is-hidden');
        overlay.classList.add('is-below-threshold');
      }

      // Set each card button's state
      overlay.querySelectorAll('.gift-popup-product').forEach(function (card) {
        var btn       = card.querySelector('[data-gift-add-btn]');
        if (!btn) return;

        // Skip buttons currently showing the momentary add-feedback animation —
        // let the 1.5 s timer handle their reset so we don't interrupt it.
        if (btn.getAttribute('data-feedback') === 'true') return;

        if (!isAbove) {
          // Cart below threshold — disable
          btn.innerHTML = SVG_BAG;
          btn.classList.remove('added', 'is-added');
          btn.disabled = true;
          btn.removeAttribute('data-gift-swap');
        } else if (remaining === 0) {
          // Quota full — check if THIS card is the one already chosen as gift
          var cardVariantId = parseInt(card.getAttribute('data-gift-variant-id'), 10);
          var isThisCardTheGift = cart && cart.items && cart.items.some(function (item) {
            return item.variant_id === cardVariantId &&
                   item.properties && item.properties['_is_gift'] === 'true';
          });

          if (isThisCardTheGift) {
            // Show check mark — still swappable if they click again
            btn.innerHTML = SVG_CHECK;
            btn.classList.add('is-added');
            btn.disabled = false;
            btn.setAttribute('data-gift-swap', 'true');
          } else {
            // Another gift is already chosen — allow swap
            btn.innerHTML = SVG_BAG;
            btn.classList.remove('added', 'is-added');
            btn.disabled = false;
            btn.setAttribute('data-gift-swap', 'true');
          }
        } else {
          // Slots still available — enable normally
          btn.innerHTML = SVG_BAG;
          btn.classList.remove('added', 'is-added');
          btn.disabled = false;
          btn.removeAttribute('data-gift-swap');
        }
      });

      overlay.classList.add('is-active');
      overlay.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';

      // Initialise (or update) the Swiper slider now that the overlay is visible
      // and has real dimensions. First call creates the instance; subsequent calls
      // just recalculate layout in case the viewport has changed.
      initGpSwiper();
    }
  
    function closeGiftPopup() {
      var overlay = document.getElementById('gift-popup-overlay');
      if (!overlay) return;
      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    }

    /**
     * Auto-open the popup only ONCE per browser session: the first time the cart
     * reaches the threshold and no gift has been chosen yet. Afterwards the
     * customer can reopen it with the "Armband wählen" link in the cart drawer.
     * (No popup below the threshold, none on every cart change.)
     */
    var AUTO_OPEN_KEY = 'balibayGiftPopupAutoOpened';
    function maybeAutoOpenOnThreshold(cart) {
      if (!cart || !cart.item_count) return;
      var allowed   = calcAllowedGifts(centsToChf(cart.total_price));
      var remaining = Math.max(allowed - countGiftsInCart(cart), 0);
      if (remaining < 1) return;
      try {
        if (window.sessionStorage && sessionStorage.getItem(AUTO_OPEN_KEY) === 'true') return;
        if (window.sessionStorage) sessionStorage.setItem(AUTO_OPEN_KEY, 'true');
      } catch (e) {}
      clearTimeout(gpPopupDelayTimer);
      gpPopupDelayTimer = setTimeout(function () {
        fetchCart().then(openGiftPopup).catch(function () { openGiftPopup(cart); });
      }, POPUP_DELAY_MS);
    }

    /**
     * Decide whether to show popup right now.
     *
     * Rules (production mode):
     *   Above threshold  → open whenever the user still has unclaimed gift slots.
     *                      Once all gifts are claimed (remaining = 0) stay silent.
     *   Below threshold  → open once per browser session only (motivational,
     *                      not nagging). Uses a separate localStorage key.
     *
     * TEST_MODE = true   → always open on every cart change, ignores all guards.
     */
    function evaluateAndMaybeShowPopup(cart) {
      var overlay = document.getElementById('gift-popup-overlay');
      if (!overlay) return;

      // Never show the popup for an empty cart
      if (!cart || !cart.item_count || cart.item_count === 0) return;

      // Cancel any previous pending popup timer — prevents stacking
      clearTimeout(gpPopupDelayTimer);

      var totalChf     = centsToChf(cart && cart.total_price);
      var allowed      = calcAllowedGifts(totalChf);
      var alreadyAdded = countGiftsInCart(cart);
      var remaining    = Math.max(allowed - alreadyAdded, 0);
      var isAbove      = allowed >= 1;

      var shouldShow = false;
      var belowKey   = LOCAL_STORAGE_KEY + '_below';

      if (TEST_MODE) {
        shouldShow = true;
      } else if (isAbove) {
        shouldShow = remaining > 0;
      } else {
        // Below threshold: once per browser session (sessionStorage resets on tab close)
        try {
          if (!window.sessionStorage || sessionStorage.getItem(belowKey) !== 'true') {
            shouldShow = true;
          }
        } catch (e) {
          shouldShow = true;
        }
      }

      if (!shouldShow) return;

      // Always fetch a FRESH cart when the timer fires so the popup reflects
      // the true cart state at display time (not the stale closure value).
      gpPopupDelayTimer = setTimeout(function () {
        fetchCart().then(function (freshCart) {
          openGiftPopup(freshCart);
          // Mark below-threshold as shown only after the popup actually opens
          if (!TEST_MODE && !isAbove) {
            try {
              if (window.sessionStorage) sessionStorage.setItem(belowKey, 'true');
            } catch (e) {}
          }
        }).catch(function () {
          // Fallback: use the original cart if fetch fails
          openGiftPopup(cart);
        });
      }, POPUP_DELAY_MS);
    }
  
    /**
     * Add gift product via /cart/add.js
     * - Then refresh Dawn cart sections and open drawer
     */
    function addGiftToCart(variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { '_is_gift': 'true' }
        })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (err) {
              throw new Error(err.description || 'Failed to add gift to cart');
            });
          }
          return response.json();
        })
        .then(function (lineItem) {
        // Update Dawn cart sections (drawer + icon) then open the drawer
        return updateDawnCartSections().then(function () {
          openDawnCartDrawer();
          return lineItem;
        });
        });
    }

    /**
     * Remove a gift from the cart by its line item key.
     * Used when the customer swaps their gift choice.
     */
    function removeGiftFromCart(itemKey) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      }).then(function (response) {
        if (!response.ok) throw new Error('Failed to remove gift from cart');
        return response.json();
      });
    }

    /**
     * When cart total drops, convert excess gift items to regular paid items
     * instead of removing them — the customer keeps their bracelet, just pays for it.
     *
     * Loop protection: _convertingGifts flag prevents re-entrance while
     * our own cart mutations are in flight.
     */
    var _cleaningGifts = false;

    function checkAndRemoveExcessGifts(cart) {
      if (_cleaningGifts) return;
      if (!cart || !cart.items) return;

      var totalChf = centsToChf(cart.total_price);
      var allowed  = calcAllowedGifts(totalChf);

      var giftItems = cart.items.filter(function (item) {
        return item.properties && item.properties['_is_gift'] === 'true';
      });

      var taken = giftItems.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);

      if (taken <= allowed) return; // Still within quota

      _cleaningGifts = true;

      var toRemove = taken - allowed;
      var chain    = Promise.resolve();

      giftItems.forEach(function (item) {
        if (toRemove <= 0) return;
        var removeQty = Math.min(item.quantity, toRemove);
        toRemove     -= removeQty;
        var newQty    = item.quantity - removeQty;
        var variantId = item.variant_id;

        // Step 1: remove (or reduce) the free gift
        chain = chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: item.key, quantity: newQty })
          }).then(function (r) { return r.json(); });
        });

        // Step 2: re-add WITHOUT _is_gift so it charges the regular price
        chain = chain.then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: variantId, quantity: removeQty })
          }).then(function (r) { return r.json(); });
        });
      });

      chain
        .then(function () {
          _cleaningGifts = false;
          // Force a full re-render of the drawer so counts are accurate
          return updateDawnCartSections();
        })
        .catch(function () {
          _cleaningGifts = false;
        });
    }

    /**
     * Dawn integration:
     * Subscribe to cart updates to auto-remove excess gifts.
     * Popup opens automatically on cart changes (fetch hook) or via
     * BalibayGiftPopup.openFromBtn() (button in the cart drawer).
     */
    function subscribeToDawnCartUpdates() {
      try {
        if (typeof subscribe !== 'function') return;
        if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;

        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          var cart = event && event.cartData;
          if (cart) {
            checkAndRemoveExcessGifts(cart);
          } else {
            fetchCart().then(function (freshCart) {
              checkAndRemoveExcessGifts(freshCart);
            });
          }
        });
      } catch (e) {}
    }

    /**
     * Fetch hook: catch cart mutations from any source and clean up excess gifts.
     */
    function installCartFetchHook() {
      if (!window.fetch) return;
      if (window.__balibayGiftCleanupFetchHookInstalled) return;
      window.__balibayGiftCleanupFetchHookInstalled = true;

      var originalFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url);
        var isCartMutation =
          typeof url === 'string' &&
          (url.indexOf('/cart/add') !== -1 ||
           url.indexOf('/cart/change') !== -1 ||
           url.indexOf('/cart/update') !== -1 ||
           url.indexOf('/cart/clear') !== -1);

        return originalFetch(input, init).then(function (response) {
          if (!isCartMutation) return response;
          setTimeout(function () {
            if (_cleaningGifts) return;
            fetchCart().then(function (cart) {
              checkAndRemoveExcessGifts(cart);
              // Open the gift selection popup ONCE when the cart first qualifies
              if (!_cleaningGifts) maybeAutoOpenOnThreshold(cart);
            }).catch(function () {});
          }, 200);
          return response;
        });
      };
    }
  
    /**
     * Initial setup:
     * - Attach event listeners
     * - Fetch cart and decide whether to show popup
     */
    function initGiftPopup() {
      var overlay = document.getElementById('gift-popup-overlay');
      if (!overlay) return; // snippet not present, nothing to do

      // Allow reset during testing via URL param
      maybeResetPopupStateFromUrl();
  
      // Close handlers
      var closeBtn = overlay.querySelector('.gift-popup-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          closeGiftPopup();
        });
      }
  
      // Clicking outside the modal closes popup
      overlay.addEventListener('click', function (event) {
        if (event.target === overlay) {
          closeGiftPopup();
        }
      });
  
      // Use event delegation on the overlay so clicks on Swiper-cloned slides
      // (which are added after init and have no direct listeners) also work.
      overlay.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-gift-add-btn]');
        if (!btn) return;

        var card = btn.closest('.gift-popup-product');
        if (!card) return;

        var variantId = card.getAttribute('data-gift-variant-id');
        if (!variantId) return;

        var numericId = parseInt(variantId, 10);
        var isSwap    = btn.getAttribute('data-gift-swap') === 'true';

        btn.disabled = true;
        btn.innerHTML = SVG_SPIN;

        var addPromise;

        if (isSwap) {
          // Remove the existing gift (identified by _is_gift property) then add the new one
          addPromise = fetchCart().then(function (freshCart) {
            var existingKey = findGiftItemKey(freshCart);
            var removeStep  = existingKey
              ? removeGiftFromCart(existingKey)
              : Promise.resolve();
            return removeStep.then(function () {
              return addGiftToCart(numericId);
            });
          });
        } else {
          addPromise = addGiftToCart(numericId);
        }

        addPromise
          .then(function () {
            // Show checkmark feedback
            btn.innerHTML = SVG_CHECK;
            btn.classList.add('is-added');
            btn.setAttribute('data-feedback', 'true');

            // After 1.5s check if all gift slots are now filled.
            // If yes → close the popup so the user can't accidentally
            // click more products thinking they're also free.
            // If slots remain (e.g. 200 CHF cart = 2 gifts) → keep open.
            setTimeout(function () {
              btn.innerHTML = SVG_BAG;
              btn.classList.remove('added', 'is-added');
              btn.disabled  = false;
              btn.removeAttribute('data-feedback');

              fetchCart().then(function (freshCart) {
                var allowed = calcAllowedGifts(centsToChf(freshCart.total_price));
                var added   = countGiftsInCart(freshCart);
                if (added >= allowed) {
                  closeGiftPopup();
                }
              });
            }, 1500);
          })
          .catch(function (err) {
            console.error(err);
            btn.innerHTML = SVG_BAG;
            btn.disabled  = false;
            alert('Das Armband konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
          });
      });
  
      // Subscribe to Dawn updates + install fallback fetch hook (no reload needed)
      subscribeToDawnCartUpdates();
      installCartFetchHook();

      // No popup on page load — it should only appear when the cart is actively
      // updated (product added/changed). An empty cart never triggers the popup.
    }
  
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initGiftPopup);
    } else {
      initGiftPopup();
    }
  
    // Expose helpers globally if you want to control from elsewhere (optional)
    window.BalibayGiftPopup = {
      fetchCart: fetchCart,
      open: openGiftPopup,
      close: closeGiftPopup,
      addGiftToCart: addGiftToCart,
      openFromBtn: function () {
        fetchCart().then(function (cart) {
          openGiftPopup(cart);
        });
      },
      reset: function () {
        try {
          if (window.localStorage)   localStorage.removeItem(LOCAL_STORAGE_KEY);
          if (window.sessionStorage) sessionStorage.removeItem(LOCAL_STORAGE_KEY + '_below');
        } catch (e) {}
      }
    };
  })();